type Color = string;
type RGBColor = [red: number, green: number, blue: number] | [red: number, green: number, blue: number, alpha: number];

const colors = {

  red: '#DF0C36',
  green: '#007B47',
  blue: '#3776CB',
  grey: '#7D7064',
  totalBlue: '#4e79c4',
  natureGreen: '#00A05D',

  burgundy: '#B00429',
  orange: '#DB7E04',
  softOrange: '#F48F04',
  pink: '#E00090',
  pinkButton: '#EA497B',
  pinkDisabledButton: '#e597ae',
  violet: '#6E2D87',

  alarmTitle: '#918b8b',
  darkBlue: '#1F489B',
  techLine: '#D35B0D',

  opacityExtensions: {
    high: 'c0',
    medium: '70',
    low: '40',
    mini: '20'
  },

  defaultStop: {
    0: '#0100AF',
    1: '#76ddf7',
    2: '#8feabf',
    3: '#FDFD03',
    4: '#FE2000',
    5: '#8D0201'
  },

  'vv-green': '#4caf50',
  'vv-red': '#ff5252',
  'vv-grey': 'grey',

  'warning-yellow': '#FFA529',

  'chart-red': '#ff0000',
  'chart-orange': '#FF803E',
  'chart-yellow': '#FFF82A',
  'chart-green': '#4caf50',
  'chart-green-max': '#008000'
};

export const priceColors = [
  colors.pinkButton,
  colors.blue,
  colors.natureGreen,
  colors.softOrange,
  '#F44D16',
  '#7371E2'
];

export const binaryColors = [
  '#888',
  colors.blue
];

export const gensetColors = [
  (gensetNumber: number) => computeIntermediaryColors('#FCAF4F', '#6B410C', gensetNumber),
  (gensetNumber: number) => computeIntermediaryColors('#F286F6', '#55165A', gensetNumber)
];

export default colors;

export function hexToRgba(hexa: Color): string | null {
  if (hexa.length !== 9) {
    return null;
  }
  const [r, g, b, a = 0] = parseHexaNumbers(hexa);
  return `rgba(${r}, ${g}, ${b}, ${a / 255})`;
}

export function hexToRgb(hexa: Color): string | null {
  if (hexa.length !== 7) {
    return null;
  }
  const [r, g, b] = parseHexaNumbers(hexa);
  return `rgb(${r}, ${g}, ${b})`;
}

export function computeIntermediaryColors(startingColor: Color, endingColor: Color, numbersToGenerate: number): Color[] {
  const [startingRed, startingGreen, startingBlue] = parseHexaNumbers(startingColor);
  const [endingRed, endingGreen, endingBlue] = parseHexaNumbers(endingColor);

  const redGradientIncrement = (endingRed - startingRed) / (numbersToGenerate - 1 || 1);
  const greenGradientIncrement = (endingGreen - startingGreen) / (numbersToGenerate - 1 || 1);
  const blueGradientIncrement = (endingBlue - startingBlue) / (numbersToGenerate - 1 || 1);

  return [...new Array(numbersToGenerate).keys()]
    .map((index): RGBColor => ([
      redGradientIncrement * index + startingRed,
      greenGradientIncrement * index + startingGreen,
      blueGradientIncrement * index + startingBlue
    ]))
    .map(convertToHexa);
}

function convertToHexa(colorRGB: RGBColor): Color {
  const hexaString = colorRGB
    .map(number => number.toFixed(0))
    .map(number => Number.parseInt(number))
    .map(number => number.toString(16))
    .map(hexa => hexa.padStart(2, '0'))
    .map(hexa => hexa.toUpperCase())
    .join('');
  return `#${hexaString}`;
}

function parseHexaNumbers(hexa: Color): RGBColor {
  return (hexa.match(/[0-9A-F]{2}/g) ?? [])
    .map(hex => Number.parseInt(hex, 16)) as RGBColor;
}
